import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useLocation, useParams } from "react-router-dom";
import {
	FiAlertTriangle,
	FiArrowLeft,
	FiCheck,
	FiCheckCircle,
	FiEdit3,
	FiRefreshCw,
	FiUploadCloud,
	FiXCircle,
	FiZap
} from "react-icons/fi";
import { usePageHeader } from "../../hooks/usePageHeader";
import { LoadingSpinner } from "../../Components/LoadingSpinner";
import { showSuccessModal } from "../../Helper/showSuccessModal";
import { client } from "../../Clients";
import {
	deployContent,
	getStatus,
	saveField,
	translateFields,
	type TranslationFieldStatus,
	type TranslationStatusResponse
} from "./translationsClient";

// Target languages the BCC can publish into; the offered switcher is intersected with the
// "languages" singleton so editors only see languages enabled for the site (mirrors /traduction).
const TARGET_LANGUAGES = [
	{ code: "en", label: "English" },
	{ code: "nl", label: "Nederlands" },
	{ code: "de", label: "Deutsch" },
	{ code: "es", label: "Español" },
	{ code: "pt", label: "Português" },
	{ code: "ar", label: "العربية" },
	{ code: "sw", label: "Swahili" },
	{ code: "ln", label: "Lingala" }
];

const STATUS_BADGES: Record<string, { label: string; className: string; icon: React.ReactNode }> = {
	ok: {
		label: "À jour",
		className: "border-emerald-200 bg-emerald-50 text-emerald-700",
		icon: <FiCheckCircle className="h-3 w-3" />
	},
	stale: {
		label: "Obsolète",
		className: "border-amber-200 bg-amber-50 text-amber-700",
		icon: <FiAlertTriangle className="h-3 w-3" />
	},
	missing: {
		label: "Manquant",
		className: "border-rose-200 bg-rose-50 text-rose-700",
		icon: <FiXCircle className="h-3 w-3" />
	}
};

const StatusBadge: React.FC<{ status: string; isManual: boolean }> = ({ status, isManual }) => {
	const badge = STATUS_BADGES[status] ?? STATUS_BADGES.missing;
	return (
		<span className="inline-flex items-center gap-1.5">
			<span
				className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-bold ${badge.className}`}
			>
				{badge.icon} {badge.label}
			</span>
			{isManual && (
				<span className="inline-flex items-center gap-1 rounded-full border border-indigo-200 bg-indigo-50 px-2 py-0.5 text-[10px] font-bold text-indigo-700">
					<FiEdit3 className="h-3 w-3" /> Manuel
				</span>
			)}
		</span>
	);
};

interface FieldRowProps {
	field: TranslationFieldStatus;
	targetLanguage: string;
	onRetranslate: (path: string) => Promise<void>;
	onSaveManual: (path: string, value: string) => Promise<void>;
	busy: boolean;
}

/**
 * One translatable field: the French source (read-only) next to the editable translation.
 * Hand edits are flagged "manuel" server-side and skipped by future AI batches.
 */
const FieldRow: React.FC<FieldRowProps> = ({ field, targetLanguage, onRetranslate, onSaveManual, busy }) => {
	const [value, setValue] = useState(field.translatedText ?? "");
	const [rowBusy, setRowBusy] = useState<"translate" | "save" | null>(null);

	// Re-sync the editor when a batch/retranslate refreshes the field from the server.
	useEffect(() => {
		setValue(field.translatedText ?? "");
	}, [field.translatedText]);

	const dirty = value !== (field.translatedText ?? "");
	const isLong = field.sourceText.length > 160;

	const retranslate = async () => {
		setRowBusy("translate");
		try {
			await onRetranslate(field.path);
		} finally {
			setRowBusy(null);
		}
	};

	const save = async () => {
		setRowBusy("save");
		try {
			await onSaveManual(field.path, value);
		} finally {
			setRowBusy(null);
		}
	};

	return (
		<div className="border-t border-slate-100 px-5 py-4 first:border-t-0">
			<div className="flex flex-wrap items-center justify-between gap-2">
				<div className="flex items-center gap-2">
					<p className="text-sm font-semibold text-slate-900">{field.label}</p>
					<code className="rounded bg-slate-100 px-1.5 py-0.5 text-[10px] text-slate-500">{field.path}</code>
				</div>
				<div className="flex items-center gap-2">
					<StatusBadge status={field.status} isManual={field.isManual} />
					<button
						type="button"
						onClick={retranslate}
						disabled={busy || rowBusy !== null}
						title="Retraduire ce champ avec l'IA"
						className="inline-flex items-center gap-1 rounded-lg border border-slate-200 bg-white px-2 py-1 text-[11px] font-semibold text-slate-600 hover:bg-slate-50 disabled:opacity-50"
					>
						<FiRefreshCw className={`h-3 w-3 ${rowBusy === "translate" ? "animate-spin" : ""}`} /> Retraduire
					</button>
					<button
						type="button"
						onClick={save}
						disabled={busy || rowBusy !== null || !dirty}
						title="Enregistrer la traduction manuelle"
						className="inline-flex items-center gap-1 rounded-lg border border-emerald-200 bg-emerald-600 px-2 py-1 text-[11px] font-semibold text-white hover:bg-emerald-700 disabled:border-slate-200 disabled:bg-white disabled:text-slate-400"
					>
						<FiCheck className="h-3 w-3" /> Enregistrer
					</button>
				</div>
			</div>
			<div className="mt-2 grid gap-3 lg:grid-cols-2">
				<div>
					<p className="mb-1 text-[10px] font-semibold uppercase tracking-wide text-slate-400">Français (source)</p>
					<div className="max-h-48 overflow-auto whitespace-pre-wrap rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-700">
						{field.sourceText}
					</div>
				</div>
				<div>
					<p className="mb-1 text-[10px] font-semibold uppercase tracking-wide text-slate-400">
						{targetLanguage.toUpperCase()}
						{dirty && <span className="ml-2 normal-case text-amber-600">modifié, non enregistré</span>}
					</p>
					<textarea
						value={value}
						onChange={(e) => setValue(e.target.value)}
						rows={isLong ? 6 : 2}
						placeholder="Pas encore traduit : le texte français sera affiché sur le site."
						className="w-full resize-y rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-800 placeholder:text-slate-400 focus:border-blue-400 focus:outline-none"
					/>
				</div>
			</div>
		</div>
	);
};

/**
 * Per-field translation workspace for one document: every translatable field with its French
 * source, current translation and status (missing / stale / ok). Batch-translates only what
 * needs work, then lets the editor republish so the language artifacts reach the CDN.
 */
const TraductionDetail: React.FC = () => {
	usePageHeader({ title: "Traduction" });

	const { schemaKey = "" } = useParams<{ schemaKey: string }>();
	const location = useLocation();
	const query = useMemo(() => new URLSearchParams(location.search), [location.search]);
	const slug = query.get("slug") ?? "";
	const lang = query.get("lang") ?? "en";

	const [status, setStatus] = useState<TranslationStatusResponse | null>(null);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState<string | null>(null);
	const [busy, setBusy] = useState(false);
	const [deploying, setDeploying] = useState<"preview" | "production" | null>(null);
	// Enabled target languages (from the "languages" singleton) for the quick switcher; null = unknown.
	const [enabledCodes, setEnabledCodes] = useState<string[] | null>(null);

	// Fragments are baked automatically on every deploy of their source content : there is no
	// per-fragment republish endpoint, so the deploy buttons only apply to pages/records.
	const isFragment = schemaKey === "fragment";

	const load = useCallback(async () => {
		setLoading(true);
		setError(null);
		try {
			setStatus(await getStatus(schemaKey, slug, lang));
		} catch (err) {
			console.error(err);
			setError("Impossible de charger le détail des traductions.");
		} finally {
			setLoading(false);
		}
	}, [schemaKey, slug, lang]);

	useEffect(() => {
		load();
	}, [load]);

	// Fetch the enabled target languages once to populate the quick switcher (mirrors /traduction).
	useEffect(() => {
		let cancelled = false;
		client.content
			.apiContentSchemaKeyLanguageGet("languages", "default")
			.then((res) => {
				const configured = (res.data?.current?.data as { languages?: Array<{ code?: string; isDisabled?: boolean }> } | undefined)
					?.languages;
				if (!cancelled && Array.isArray(configured)) {
					setEnabledCodes(configured.filter((l) => l?.code && !l.isDisabled).map((l) => l.code!.trim().toLowerCase()));
				}
			})
			.catch(() => {
				/* keep the full list on error */
			});
		return () => {
			cancelled = true;
		};
	}, []);

	// Switcher options: enabled targets, always including the language currently being edited so its
	// pill stays visible even if it isn't in the enabled set.
	const switcherLanguages = useMemo(() => {
		const base = enabledCodes ? TARGET_LANGUAGES.filter((l) => enabledCodes.includes(l.code)) : TARGET_LANGUAGES;
		if (lang && !base.some((l) => l.code === lang)) {
			return [...base, { code: lang, label: lang.toUpperCase() }];
		}
		return base;
	}, [enabledCodes, lang]);

	const counts = useMemo(() => {
		const fields = status?.fields ?? [];
		return {
			ok: fields.filter((f) => f.status === "ok").length,
			stale: fields.filter((f) => f.status === "stale").length,
			missing: fields.filter((f) => f.status === "missing").length,
			manual: fields.filter((f) => f.isManual).length,
			toTranslate: fields.filter((f) => (f.status === "missing" || f.status === "stale") && !f.isManual).length
		};
	}, [status]);

	const translateBatch = useCallback(async () => {
		setBusy(true);
		setError(null);
		try {
			const result = await translateFields(schemaKey, slug, lang);
			if (!result.success) {
				setError(result.errorMessage ?? "La traduction a échoué.");
			} else if (result.translatedCount === 0) {
				showSuccessModal({ title: "Rien à traduire", message: "Tous les champs sont déjà à jour." });
			} else {
				showSuccessModal({
					title: "Traduction terminée",
					message: `${result.translatedCount} champ${result.translatedCount > 1 ? "s" : ""} traduit${
						result.translatedCount > 1 ? "s" : ""
					}. Republiez le contenu pour mettre le site à jour.`
				});
			}
			await load();
		} catch (err) {
			console.error(err);
			setError("La traduction a échoué.");
		} finally {
			setBusy(false);
		}
	}, [schemaKey, slug, lang, load]);

	const retranslateOne = useCallback(
		async (path: string) => {
			setError(null);
			try {
				const result = await translateFields(schemaKey, slug, lang, [path]);
				if (!result.success) setError(result.errorMessage ?? "La traduction a échoué.");
				await load();
			} catch (err) {
				console.error(err);
				setError("La traduction a échoué.");
			}
		},
		[schemaKey, slug, lang, load]
	);

	const saveManual = useCallback(
		async (path: string, value: string) => {
			setError(null);
			try {
				await saveField(schemaKey, slug, lang, path, value);
				await load();
			} catch (err) {
				console.error(err);
				setError("Impossible d'enregistrer la traduction.");
			}
		},
		[schemaKey, slug, lang, load]
	);

	const deploy = useCallback(
		async (target: "preview" | "production") => {
			setDeploying(target);
			setError(null);
			try {
				await deployContent(schemaKey, slug, target);
				showSuccessModal({
					title: target === "preview" ? "Preview republié" : "Production republiée",
					message: "Les traductions sont désormais visibles sur le site."
				});
			} catch (err) {
				console.error(err);
				setError(`La republication ${target} a échoué.`);
			} finally {
				setDeploying(null);
			}
		},
		[schemaKey, slug]
	);

	return (
		<div className="min-h-full space-y-6 overflow-x-hidden p-4 sm:p-6">
			{/* Header */}
			<div className="flex flex-wrap items-center justify-between gap-3">
				<div className="flex items-center gap-3">
					<Link
						to="/traduction"
						className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-semibold text-slate-700 shadow-sm hover:bg-slate-50"
					>
						<FiArrowLeft className="h-4 w-4" /> Retour
					</Link>
					<div>
						<h1 className="text-2xl font-bold text-slate-900">{status?.title || slug || schemaKey}</h1>
						<p className="text-sm text-slate-500">
							<span className="font-semibold uppercase">{schemaKey}</span>
							{slug && <span> · /{slug}</span>}
							<span>
								{" "}
								· {(status?.sourceLanguage ?? "fr").toUpperCase()} → {(status?.targetLanguage ?? lang).toUpperCase()}
							</span>
						</p>
						{switcherLanguages.length > 1 && (
							<div className="mt-2 flex flex-wrap items-center gap-1.5">
								<span className="text-xs font-medium text-slate-400">Langue cible :</span>
								{switcherLanguages.map((l) => {
									const active = l.code === lang;
									return (
										<Link
											key={l.code}
											to={`/traduction/${schemaKey}?slug=${encodeURIComponent(slug)}&lang=${l.code}`}
											aria-current={active ? "true" : undefined}
											title={l.label}
											className={`inline-flex items-center rounded-md border px-2 py-0.5 text-xs font-semibold uppercase transition ${
												active
													? "border-blue-600 bg-blue-600 text-white shadow-sm"
													: "border-slate-200 bg-white text-slate-600 hover:bg-slate-50"
											}`}
										>
											{l.code}
										</Link>
									);
								})}
							</div>
						)}
					</div>
				</div>
				<div className="flex flex-wrap items-center gap-2">
					<button
						type="button"
						onClick={translateBatch}
						disabled={busy || loading || counts.toTranslate === 0}
						className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-blue-700 disabled:opacity-50"
					>
						<FiZap className={`h-4 w-4 ${busy ? "animate-pulse" : ""}`} />
						{busy ? "Traduction en cours…" : `Traduire manquants + obsolètes (${counts.toTranslate})`}
					</button>
					{!isFragment && (
						<>
							<button
								type="button"
								onClick={() => deploy("preview")}
								disabled={deploying !== null || busy}
								className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-semibold text-slate-700 shadow-sm hover:bg-slate-50 disabled:opacity-50"
							>
								<FiUploadCloud className="h-4 w-4" />
								{deploying === "preview" ? "Republication…" : "Republier preview"}
							</button>
							<button
								type="button"
								onClick={() => deploy("production")}
								disabled={deploying !== null || busy}
								className="inline-flex items-center gap-2 rounded-lg border border-emerald-200 bg-emerald-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-emerald-700 disabled:opacity-50"
							>
								<FiUploadCloud className="h-4 w-4" />
								{deploying === "production" ? "Republication…" : "Republier production"}
							</button>
						</>
					)}
				</div>
			</div>

			{error && <div className="rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">{error}</div>}

			{isFragment && !loading && (
				<div className="rounded-xl border border-sky-200 bg-sky-50 px-4 py-3 text-sm text-sky-800">
					Ce fragment est re-publié automatiquement à chaque déploiement de son contenu source : les
					traductions enregistrées ici seront matérialisées sans action supplémentaire.
				</div>
			)}

			{/* Summary chips */}
			{!loading && status && (
				<div className="flex flex-wrap gap-2">
					<span className="inline-flex items-center gap-1 rounded-full border border-emerald-200 bg-emerald-50 px-2.5 py-1 text-xs font-bold text-emerald-700">
						<FiCheckCircle className="h-3.5 w-3.5" /> {counts.ok} à jour
					</span>
					<span className="inline-flex items-center gap-1 rounded-full border border-amber-200 bg-amber-50 px-2.5 py-1 text-xs font-bold text-amber-700">
						<FiAlertTriangle className="h-3.5 w-3.5" /> {counts.stale} obsolètes
					</span>
					<span className="inline-flex items-center gap-1 rounded-full border border-rose-200 bg-rose-50 px-2.5 py-1 text-xs font-bold text-rose-700">
						<FiXCircle className="h-3.5 w-3.5" /> {counts.missing} manquants
					</span>
					<span className="inline-flex items-center gap-1 rounded-full border border-indigo-200 bg-indigo-50 px-2.5 py-1 text-xs font-bold text-indigo-700">
						<FiEdit3 className="h-3.5 w-3.5" /> {counts.manual} manuels
					</span>
				</div>
			)}

			{/* Field rows */}
			{loading ? (
				<div className="flex items-center justify-center py-16">
					<LoadingSpinner size="lg" />
				</div>
			) : !status || status.fields.length === 0 ? (
				<div className="rounded-2xl border border-dashed border-slate-300 bg-slate-50 px-6 py-16 text-center text-sm text-slate-500">
					Aucun champ traduisible pour ce contenu.
				</div>
			) : (
				<div className="rounded-2xl border border-slate-200 bg-white shadow-sm">
					{status.fields.map((field) => (
						<FieldRow
							key={field.path}
							field={field}
							targetLanguage={lang}
							onRetranslate={retranslateOne}
							onSaveManual={saveManual}
							busy={busy}
						/>
					))}
				</div>
			)}
		</div>
	);
};

export default TraductionDetail;
